import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
 User,
 ArrowLeft,
 Mail,
 Phone,
 Edit,
 Save,
 X,
 CheckCircle,
 AlertCircle,
 Plus,
 Trash2,
 Star
} from 'lucide-react'
import { Button, PasswordInput, Card, CardHeader, CardTitle, CardContent, ModernInput, ModernButton } from '../../components/ui'
import { useUserRoles } from '../../hooks/useUserRoles'
import { useUsers } from '../../hooks/useUsers'
import { UserPhone } from '../../types'

export const UserProfilePage: React.FC = () => {
 const navigate = useNavigate()
 const { t } = useTranslation()
 const { id } = useParams<{ id: string }>()
 const { roles, isLoading: rolesLoading } = useUserRoles()
 const { getUserById, updateUser } = useUsers()
 
 const [userData, setUserData] = useState<any>(null)
 const [isLoading, setIsLoading] = useState(false)
 const [isEditing, setIsEditing] = useState(false)
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [errors, setErrors] = useState<Record<string, string>>({})
 const [successMessage, setSuccessMessage] = useState('')

 const [formData, setFormData] = useState({
 name: '',
 email: '',
 password: '',
 confirmPassword: '',
 dealershipRole: '',
 phones: [] as UserPhone[],
 isActive: true
 })

 // Carrega dados do usuário
 useEffect(() => {
 const loadUserData = async () => {
 if (!id) return

 setIsLoading(true)
 try {
 const user = await getUserById(id)
 setUserData(user)
 setFormData({
 name: user.name || '',
 email: user.email || '',
 password: '',
 confirmPassword: '',
 dealershipRole: user.dealershipRole || user.role?.name || '',
 phones: user.phones || [],
 isActive: user.isActive ?? true
 })
 } catch (error) {
 console.error('Erro ao carregar dados do usuário:', error)
 } finally {
 setIsLoading(false)
 }
 }

 loadUserData()
 }, [id]) // Removido getUserById das dependências

 const validateForm = () => {
 const newErrors: Record<string, string> = {}

 if (!formData.name.trim()) {
 newErrors.name = t('userRegistration.validation.nameRequired')
 }

 if (!formData.email.trim()) {
 newErrors.email = t('userRegistration.validation.emailRequired')
 } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
 newErrors.email = t('userRegistration.validation.emailInvalid')
 }

 // Validação de senha só se for preenchida
 if (formData.password && formData.password.length < 6) {
 newErrors.password = t('userRegistration.validation.passwordMin')
 }
 if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
 newErrors.confirmPassword = t('userRegistration.validation.passwordMismatch')
 }

 if (!formData.dealershipRole) {
 newErrors.role = t('userRegistration.validation.roleRequired')
 }

 if (!formData.phones || formData.phones.length === 0) {
 newErrors.phones = t('userProfile.validation.phonesRequired')
 } else {
 formData.phones.forEach((phone, index) => {
 if (!phone.area.trim()) {
 newErrors[`phone_${index}_area`] = t('userRegistration.validation.areaRequired')
 }
 if (!phone.phone.trim()) {
 newErrors[`phone_${index}_phone`] = t('userRegistration.validation.phoneRequired')
 }
 })
 }

 setErrors(newErrors)
 return Object.keys(newErrors).length === 0
 }

 const handleInputChange = (field: string, value: string | boolean) => {
 setFormData(prev => ({ ...prev, [field]: value }))
 // Limpa erro do campo quando usuário começa a digitar
 if (errors[field]) {
 setErrors(prev => ({ ...prev, [field]: '' }))
 }
 }

 const addPhone = () => {
 const newPhone: UserPhone = {
 codeArea: '+55',
 area: '',
 phone: '',
 isPrimary: formData.phones.length === 0
 }
 setFormData(prev => ({
 ...prev,
 phones: [...prev.phones, newPhone]
 }))
 }

 const removePhone = (index: number) => {
 setFormData(prev => {
 const newPhones = prev.phones.filter((_, i) => i !== index)
 // Se removemos o telefone primário, torna o primeiro restante como primário
 if (newPhones.length > 0 && prev.phones[index].isPrimary) {
 newPhones[0].isPrimary = true
 }
 return { ...prev, phones: newPhones }
 })
 }

 const updatePhone = (index: number, field: keyof UserPhone, value: string | boolean) => {
 setFormData(prev => ({
 ...prev,
 phones: prev.phones.map((phone, i) => 
 i === index ? { ...phone, [field]: value } : phone
 )
 }))
 // Limpa erros relacionados a este telefone
 const errorKey = `phone_${index}_${field}`
 if (errors[errorKey]) {
 setErrors(prev => ({ ...prev, [errorKey]: '' }))
 }
 }

 const setPrimaryPhone = (index: number) => {
 setFormData(prev => ({
 ...prev,
 phones: prev.phones.map((phone, i) => ({
 ...phone,
 isPrimary: i === index
 }))
 }))
 }

 const getSelectedRole = () => {
 return roles.find(r => r.value === formData.dealershipRole)
 }

 const getRoleDisplayName = (roleValue: string) => {
 const selectedRole = roles.find(r => r.value === roleValue)
 return selectedRole?.label || roleValue || t('common.notProvided')
 }

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: '2-digit',
 year: 'numeric'
 })
 }

 const handleSave = async () => {
 if (!validateForm() || !id) return

 setIsSubmitting(true)
 try {
 const updateData = {
 name: formData.name,
 email: formData.email,
 password: formData.password || undefined,
 phones: formData.phones,
 dealershipRole: formData.dealershipRole,
 isActive: formData.isActive
 }

 await updateUser(id, updateData)
 
 setSuccessMessage(t('userProfile.successUpdate'))
 setIsEditing(false)
 
 // Atualiza os dados locais
 setUserData({
 ...userData,
 ...formData,
 phones: formData.phones
 })
 } catch (error) {
 console.error('Erro ao atualizar perfil:', error)
 // Aqui você pode adicionar um toast de erro
 } finally {
 setIsSubmitting(false)
 }
 }

 const handleCancel = () => {
 // Restaura os dados originais
 setFormData({
 name: userData?.name || '',
 email: userData?.email || '',
 password: '',
 confirmPassword: '',
 dealershipRole: userData?.dealershipRole || userData?.role?.name || '',
 phones: userData?.phones || [],
 isActive: userData?.isActive ?? true
 })
 setErrors({})
 setIsEditing(false)
 }

 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-screen">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
 </div>
 )
 }

 if (!userData) {
  return (
   <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
     <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
     <h2 className="text-xl font-semibold text-foreground mb-2">
 {t('userProfile.notFoundTitle')}
     </h2>
     <p className="text-muted-foreground mb-4">
 {t('userProfile.notFoundDescription')}
     </p>
 <Button onClick={() => navigate('/users')}>
 {t('userProfile.backToList')}
  </Button>
 </div>
</div>
 )
 }

 return (
 <div className="max-w-4xl mx-auto space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-4">
 <Button
  variant="outline"
  onClick={() => navigate('/users')}
 >
  <ArrowLeft className="h-4 w-4 mr-2" />
 {t('userProfile.back')}
 </Button>
 <div>
  <h1 className="text-2xl font-bold text-foreground">
 {t('userProfile.title')}
  </h1>
  <p className="text-muted-foreground">
 {t('userProfile.subtitle')}
  </p>
 </div>
 </div>
 
 {!isEditing && (
 <Button onClick={() => setIsEditing(true)}>
  <Edit className="h-4 w-4 mr-2" />
 {t('userProfile.edit')}
 </Button>
 )}
 </div>

 {/* Mensagem de Sucesso */}
 {successMessage && (
 <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
 <div className="flex items-center">
 <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
 <p className="text-green-800 dark:text-green-200">{successMessage}</p>
 </div>
 </div>
 )}

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 {/* Informações do Usuário */}
 <div className="lg:col-span-1">
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <User className="h-5 w-5 mr-2" />
 {t('userProfile.basicInfoTitle')}
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="flex items-center justify-center mb-6">
 <div className="w-20 h-20 bg-accent/10 dark:bg-primary-900 rounded-full flex items-center justify-center">
 <User className="h-10 w-10 text-accent dark:text-accent" />
 </div>
 </div>
 
 <div className="space-y-3">
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userRegistration.nameLabel')}
 </label>
 <p className="text-lg font-semibold text-foreground">
 {userData.name}
 </p>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userRegistration.emailLabel')}
 </label>
 <p className="text-foreground">{userData.email}</p>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userProfile.phonesLabel')}
 </label>
 <div className="space-y-1">
 {userData.phones && userData.phones.length > 0 ? (
 userData.phones.map((phone: any, index: number) => (
 <div key={index} className="flex items-center space-x-2">
 <p className="text-foreground">
 {phone.codeArea} ({phone.area}) {phone.phone}
 </p>
 {phone.isPrimary && (
 <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
 <Star className="h-2 w-2 mr-1" />
 {t('userProfile.primaryBadge')}
 </span>
 )}
 </div>
 ))
 ) : (
 <p className="text-muted-foreground">{t('userProfile.noPhones')}</p>
 )}
 </div>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userProfile.roleLabel')}
 </label>
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
 {getRoleDisplayName(userData.dealershipRole || userData.role?.name)}
 </span>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userProfile.userStatusLabel')}
 </label>
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
 userData.isActive 
 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
 : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
 }`}>
 {userData.isActive ? t('userRegistration.active') : t('userRegistration.inactive')}
 </span>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userProfile.createdAtLabel')}
 </label>
 <p className="text-foreground">
 {formatDate(userData.createdAt)}
 </p>
 </div>
 
 {userData.lastLogin && (
 <div>
 <label className="block text-sm font-medium text-muted-foreground">
 {t('userProfile.lastLoginLabel')}
 </label>
 <p className="text-foreground">
 {formatDate(userData.lastLogin)}
 </p>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Formulário de Edição */}
 <div className="lg:col-span-2">
 <Card>
 <CardHeader>
 <CardTitle>
 {isEditing ? t('userProfile.editInfoTitle') : t('userProfile.detailsTitle')}
 </CardTitle>
 </CardHeader>
 <CardContent>
 {isEditing ? (
 <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
 {/* Nome e Email */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <ModernInput
 label={t('userRegistration.nameLabel')}
 value={formData.name}
 onChange={(e) => handleInputChange('name', e.target.value)}
 placeholder={t('userProfile.namePlaceholder')}
 error={errors.name}
 required
 leftIcon={<User className="h-4 w-4" />}
 />
 <ModernInput
 label={t('userRegistration.emailLabel')}
 type="email"
 value={formData.email}
 onChange={(e) => handleInputChange('email', e.target.value)}
 placeholder={t('userProfile.emailPlaceholder')}
 error={errors.email}
 required
 leftIcon={<Mail className="h-4 w-4" />}
 />
 </div>

 {/* Senhas */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <PasswordInput
 label={t('userProfile.newPasswordLabel')}
 value={formData.password}
 onChange={(e) => handleInputChange('password', e.target.value)}
 placeholder={t('userProfile.newPasswordPlaceholder')}
 error={errors.password}
 />
 <PasswordInput
 label={t('userProfile.confirmNewPasswordLabel')}
 value={formData.confirmPassword}
 onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
 placeholder={t('userProfile.confirmNewPasswordPlaceholder')}
 error={errors.confirmPassword}
 />
 </div>

 {/* {t('userProfile.phonesLabel')} */}
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <label className="block text-sm font-medium text-foreground">
 {t('userProfile.phonesLabel')}
 </label>
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={addPhone}
 className="flex items-center"
 >
 <Plus className="h-4 w-4 mr-1" />
 {t('userProfile.addPhone')}
 </Button>
 </div>
 
 {errors.phones && (
 <p className="text-red-600 dark:text-red-400 text-sm">{errors.phones}</p>
 )}

 {formData.phones.map((phone, index) => (
 <div key={index} className="border border-border rounded-lg p-4 space-y-3">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-2">
 <Phone className="h-4 w-4 text-muted-foreground" />
 <span className="text-sm font-medium text-foreground">
 {t('userProfile.phoneItemLabel', { number: index + 1 })}
 </span>
 {phone.isPrimary && (
 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
 <Star className="h-3 w-3 mr-1" />
 {t('userProfile.primaryBadge')}
 </span>
 )}
 </div>
 <div className="flex items-center space-x-2">
 {!phone.isPrimary && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => setPrimaryPhone(index)}
 className="text-xs"
 >
 <Star className="h-3 w-3 mr-1" />
 {t('userProfile.makePrimary')}
 </Button>
 )}
 {formData.phones.length > 1 && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={() => removePhone(index)}
 className="text-red-600 hover:text-red-700 hover:bg-red-50"
 >
 <Trash2 className="h-3 w-3" />
 </Button>
 )}
 </div>
 </div>
 
 <div className="grid grid-cols-3 gap-4">
 <div>
 <ModernInput
 label={t('userProfile.countryCodeLabel')}
 value={phone.codeArea}
 onChange={(e) => updatePhone(index, 'codeArea', e.target.value)}
 placeholder={t('userProfile.countryCodePlaceholder')}
 disabled
 />
 </div>
 <div>
 <ModernInput
 label={t('userProfile.areaLabel')}
 value={phone.area}
 onChange={(e) => updatePhone(index, 'area', e.target.value)}
 placeholder={t('userProfile.areaPlaceholder')}
 error={errors[`phone_${index}_area`]}
 required
 />
 </div>
 <div>
 <ModernInput
 label={t('userProfile.phoneLabel')}
 value={phone.phone}
 onChange={(e) => updatePhone(index, 'phone', e.target.value)}
 placeholder={t('userProfile.phonePlaceholder')}
 error={errors[`phone_${index}_phone`]}
 required
 />
 </div>
 </div>
 </div>
 ))}

 {formData.phones.length === 0 && (
 <div className="text-center py-8 text-muted-foreground">
 <Phone className="h-12 w-12 mx-auto mb-4 opacity-50" />
 <p>{t('userProfile.noPhonesAdded')}</p>
 <p className="text-sm">{t('userProfile.addPhoneToStart')}</p>
 </div>
 )}
 </div>

 {/* {t('userProfile.userStatusLabel')} */}
 <div className="space-y-2">
 <label className="block text-sm font-medium text-foreground">
 {t('userProfile.userStatusLabel')}
 </label>
 <div className="flex items-center space-x-4">
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 checked={formData.isActive === true}
 onChange={() => handleInputChange('isActive', true)}
 className="mr-2 text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">{t('userRegistration.active')}</span>
 </label>
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 checked={formData.isActive === false}
 onChange={() => handleInputChange('isActive', false)}
 className="mr-2 text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">{t('userRegistration.inactive')}</span>
 </label>
 </div>
 </div>

 {/* {t('userProfile.roleLabel')} */}
 <div className="space-y-2">
 <label className="block text-sm font-medium text-foreground">
 {t('userProfile.roleLabel')} *
 </label>
 {rolesLoading ? (
 <div className="animate-pulse bg-gray-200 h-10 rounded"></div>
 ) : (
 <select
 value={formData.dealershipRole}
 onChange={(e) => handleInputChange('dealershipRole', e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent dark:text-white"
 required
 >
 <option value="">{t('userProfile.selectProfile')}</option>
 {roles.map((role) => (
 <option key={role.value} value={role.value}>
 {role.label}
 </option>
 ))}
 </select>
 )}
 {errors.role && (
 <p className="text-red-600 dark:text-red-400 text-sm">{errors.role}</p>
 )}
 
 {getSelectedRole() && (
 <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
 <p className="text-sm text-blue-800 dark:text-blue-200">
 <strong>{getSelectedRole()?.label}:</strong> {getSelectedRole()?.description}
 </p>
 </div>
 )}
 </div>

 {/* Botões */}
 <div className="flex items-center space-x-3 pt-4">
 <ModernButton
 type="submit"
 loading={isSubmitting}
 className="flex items-center"
 >
 <Save className="h-4 w-4 mr-2" />
 {t('userRegistration.submitEdit')}
 </ModernButton>
 <Button
 type="button"
 variant="outline"
 onClick={handleCancel}
 className="flex items-center"
 >
 <X className="h-4 w-4 mr-2" />
 {t('userRegistration.cancel')}
 </Button>
 </div>
 </form>
 ) : (
 <div className="space-y-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-1">
 {t('userRegistration.nameLabel')}
 </label>
 <p className="text-foreground">{userData.name}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-1">
 {t('userRegistration.emailLabel')}
 </label>
 <p className="text-foreground">{userData.email}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-1">
 {t('userProfile.phonesLabel')}
 </label>
 <div className="space-y-1">
 {userData.phones && userData.phones.length > 0 ? (
 userData.phones.map((phone: any, index: number) => (
 <div key={index} className="flex items-center space-x-2">
 <p className="text-foreground">
 {phone.codeArea} ({phone.area}) {phone.phone}
 </p>
 {phone.isPrimary && (
 <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
 <Star className="h-2 w-2 mr-1" />
 {t('userProfile.primaryBadge')}
 </span>
 )}
 </div>
 ))
 ) : (
 <p className="text-muted-foreground">{t('userProfile.noPhones')}</p>
 )}
 </div>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-1">
 {t('userProfile.roleLabel')}
 </label>
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
 {getRoleDisplayName(userData.dealershipRole || userData.role?.name)}
 </span>
 </div>
 </div>
 
 {getSelectedRole() && (
 <div className="p-4 bg-surface rounded-lg">
 <h4 className="font-medium text-foreground mb-2">
 {t('userProfile.profileDescriptionTitle')}
 </h4>
 <p className="text-sm text-muted-foreground">
 {getSelectedRole()?.description}
 </p>
 </div>
 )}
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 </div>
 )
}
